/**
 * Motor del BANDIT CONTEXTUAL lineal (funciones PURAS, sin estado ni I/O).
 *
 * FUENTE ÚNICA compartida entre el ENTRENADOR (servidor, vía apps/web/src/lib/bandit.ts
 * que re-exporta esto) y el SDK del navegador (vía el subpath puro
 * `@tracking-web/shared/banditMath`, sin arrastrar zod). Que el SDK codifique `x` y
 * muestree Thompson con EXACTAMENTE la misma matemática que entrenó el posterior es un
 * requisito duro: si divergen, el bandit se rompe en silencio. El SDK solo usa la parte
 * de SERVIR (buildEncoding/encodeContext/serve…); tsup hace tree-shaking del resto.
 *
 * A diferencia del motor A/B (que corona UN ganador global con mSPRT), el bandit
 * sirve la mejor variante PARA CADA visitante y nunca cierra: explora y se adapta
 * en continuo. La recompensa es CONTINUA (IPVPS winsorizado), así que el modelo es
 * lineal (predice el IPVPS esperado), no logístico.
 *
 * Modelo: por cada variante (arm) `k` una regresión lineal bayesiana sobre un vector
 * de contexto `x` (intercepto + one-hot de pocas features categóricas):
 *   · A_k = diag(prior) + Σ xᵢ·xᵢᵀ      (matriz de precisión; prior = ridge λ)
 *   · b_k = Σ rᵢ·xᵢ                      (rᵢ = recompensa de la sesión que vio `k`)
 *   · θ_k = A_k⁻¹ · b_k                  (pesos; IPVPS esperado de `k` para `x` = θ_k·x)
 *
 * DEGRADACIÓN ELEGANTE (la idea clave de la spec, §2.3): el ridge sobre las
 * dimensiones de CONTEXTO es fuerte, pero sobre el INTERCEPTO es flojo. Con pocos
 * datos los coeficientes de contexto quedan encogidos a ~0 → la predicción ≈ el
 * intercepto (IPVPS medio de la variante en esa URL) → el bandit "actúa por
 * promedio" sin segmentar. Conforme llega evidencia, los coeficientes de contexto
 * "despiertan" → emerge la personalización. Sin código aparte: es el mismo modelo.
 *
 * EXPLORACIÓN: Thompson sampling al servir. Para un `x` dado, el score de cada arm
 * es una gaussiana 1-D:  θ̃_k·x ~ N(μ_k, σ_k²) con μ_k = θ_k·x y σ_k² = α²·xᵀA_k⁻¹x
 * (función lineal de una gaussiana → gaussiana; los arms son independientes porque
 * cada uno entrena solo con SUS impresiones). Por eso el cliente NO necesita
 * invertir matrices ni Cholesky: muestrea UN escalar por arm y coge el argmax.
 *
 * Todo aquí es determinista dado el PRNG: ningún `Math.random()`, para que el gate
 * de fiabilidad (scripts/stats-check.ts) sea reproducible bit a bit.
 */

/* ─────────────────────────── Tipos base ─────────────────────────── */

export type Vector = number[];
export type Matrix = number[][];

/** Una feature categórica de contexto. El PRIMER nivel es la referencia (se omite
 *  del one-hot): su efecto queda absorbido por el intercepto. */
export interface FeatureSpec {
  name: string;
  levels: string[];
}

/** Codificación fija: dado el catálogo de features, sabe mapear un contexto a `x`. */
export interface BanditEncoding {
  features: FeatureSpec[];
  dim: number; // 1 (intercepto) + Σ (niveles − 1)
  // offset[f] = índice en x del primer dummy de la feature f; map[f][nivel] = índice
  // del dummy (o -1 si es el nivel de referencia / desconocido).
  offsets: number[];
  levelIndex: Array<Record<string, number>>;
}

/** Construye la codificación (intercepto + one-hot drop-first) desde el catálogo. */
export function buildEncoding(features: FeatureSpec[]): BanditEncoding {
  const offsets: number[] = [];
  const levelIndex: Array<Record<string, number>> = [];
  let dim = 1; // intercepto en x[0]
  for (const f of features) {
    offsets.push(dim);
    const map: Record<string, number> = {};
    // El nivel de referencia (levels[0]) NO tiene dummy: índice -1.
    f.levels.forEach((lvl, i) => {
      map[lvl] = i === 0 ? -1 : dim + (i - 1);
    });
    levelIndex.push(map);
    dim += Math.max(0, f.levels.length - 1);
  }
  return { features, dim, offsets, levelIndex };
}

/** Codifica un contexto a `x`. Un nivel desconocido cae a referencia (todos 0). */
export function encodeContext(enc: BanditEncoding, ctx: Record<string, string>): Vector {
  const x = new Array(enc.dim).fill(0);
  x[0] = 1; // intercepto
  for (let f = 0; f < enc.features.length; f++) {
    const name = enc.features[f].name;
    const lvl = ctx[name];
    if (lvl == null) continue;
    const idx = enc.levelIndex[f][lvl];
    if (idx != null && idx >= 0) x[idx] = 1;
  }
  return x;
}

/**
 * Vector de prior ridge (diagonal de A inicial). Flojo en el intercepto (`lambda0`,
 * para que el promedio de la URL se aprenda rápido) y fuerte en las dimensiones de
 * contexto (`lambdaCtx`, para no inventar personalización con pocos datos). Ambos
 * > 0 garantizan que A sea invertible incluso con 0 observaciones (cold-start).
 */
export function ridgePrior(enc: BanditEncoding, lambda0: number, lambdaCtx: number): Vector {
  const p = new Array(enc.dim).fill(lambdaCtx);
  p[0] = lambda0;
  return p;
}

/* ─────────────────────────── Acumulador por arm (sufficient stats) ─────────────────────────── */

/**
 * Estadísticos suficientes de un arm: A (precisión), b, y contadores. Se acumulan
 * online (un `observe` por impresión cerrada) y se "resuelven" en lote en el
 * entrenador. NO se pondera por propensity: la regresión por-arm E[r | x] es
 * insesgada sin pesos (la política elige el arm en función de `x`, sin confundido).
 * La propensity es para la EVALUACIÓN off-policy, no para este ajuste.
 */
export interface ArmAccumulator {
  dim: number;
  A: Matrix;
  b: Vector;
  n: number;
  sumR: number;
  sumR2: number;
}

export function createAccumulator(dim: number, prior: Vector): ArmAccumulator {
  const A: Matrix = [];
  for (let i = 0; i < dim; i++) {
    const row = new Array(dim).fill(0);
    row[i] = prior[i];
    A.push(row);
  }
  return { dim, A, b: new Array(dim).fill(0), n: 0, sumR: 0, sumR2: 0 };
}

/** Acumula una observación (x, r) en el arm: A += x·xᵀ, b += r·x. */
export function observe(acc: ArmAccumulator, x: Vector, r: number): void {
  for (let i = 0; i < acc.dim; i++) {
    const xi = x[i];
    if (xi !== 0) {
      acc.b[i] += r * xi;
      const Ai = acc.A[i];
      for (let j = 0; j < acc.dim; j++) {
        const xj = x[j];
        if (xj !== 0) Ai[j] += xi * xj;
      }
    }
  }
  acc.n++;
  acc.sumR += r;
  acc.sumR2 += r * r;
}

/* ─────────────────────────── Posterior y predicción ─────────────────────────── */

/** Posterior de un arm: θ (pesos) y A⁻¹ (covarianza no escalada). `n` = nº impresiones. */
export interface ArmPosterior {
  theta: Vector;
  Ainv: Matrix;
  n: number;
}

/** Resuelve el ridge cerrado: A⁻¹ y θ = A⁻¹·b. Una sola inversión por arm. */
export function solveArm(acc: ArmAccumulator): ArmPosterior {
  const Ainv = invert(acc.A);
  return { theta: matVec(Ainv, acc.b), Ainv, n: acc.n };
}

/** IPVPS esperado de un arm para un contexto: μ = θ·x. */
export function predict(post: ArmPosterior, x: Vector): number {
  return dot(post.theta, x);
}

/** Varianza del score de Thompson para ese contexto: σ² = α²·xᵀA⁻¹x (≥ 0). */
export function thompsonVar(post: ArmPosterior, x: Vector, alpha: number): number {
  return Math.max(0, alpha * alpha * quadForm(post.Ainv, x));
}

/** Score muestreado de Thompson: μ + σ·z, con z ~ N(0,1) provisto por quien llama. */
export function sampleScore(post: ArmPosterior, x: Vector, alpha: number, z: number): number {
  return predict(post, x) + Math.sqrt(thompsonVar(post, x, alpha)) * z;
}

/* ─────────────────────────── Servir (decisión en el navegador) ─────────────────────────── */

/** Argmax del score muestreado (un z por arm). Es la decisión de Thompson. */
export function sampleArm(posteriors: ArmPosterior[], x: Vector, alpha: number, rng: () => number): number {
  let best = 0;
  let bestScore = -Infinity;
  for (let k = 0; k < posteriors.length; k++) {
    const s = sampleScore(posteriors[k], x, alpha, gaussian(rng));
    if (s > bestScore) {
      bestScore = s;
      best = k;
    }
  }
  return best;
}

/** Política greedy (sin exploración): argmax de la media μ = θ·x. */
export function greedyArm(posteriors: ArmPosterior[], x: Vector): number {
  let best = 0;
  let bestMu = -Infinity;
  for (let k = 0; k < posteriors.length; k++) {
    const mu = predict(posteriors[k], x);
    if (mu > bestMu) {
      bestMu = mu;
      best = k;
    }
  }
  return best;
}

/**
 * Propensity MARGINAL de cada arm para un contexto: P(arm k = argmax) bajo el
 * sorteo de Thompson, estimada por Monte-Carlo. Es lo que se loguea (clip incluido
 * fuera) para la evaluación off-policy insesgada. No tiene forma cerrada con >2
 * arms, pero el MC es trivial.
 */
export function propensities(
  posteriors: ArmPosterior[],
  x: Vector,
  alpha: number,
  rng: () => number,
  draws: number
): Vector {
  const counts = new Array(posteriors.length).fill(0);
  for (let d = 0; d < draws; d++) {
    counts[sampleArm(posteriors, x, alpha, rng)]++;
  }
  return counts.map((c) => c / draws);
}

/** Muestrea un índice de una categórica dada por `probs` (suman ~1). */
export function sampleCategorical(probs: Vector, rng: () => number): number {
  const u = rng();
  let acc = 0;
  for (let i = 0; i < probs.length; i++) {
    acc += probs[i];
    if (u < acc) return i;
  }
  return probs.length - 1;
}

export interface ServeResult {
  index: number;
  propensity: number;
}

/**
 * Sirve una variante para `x`, STICKY por `seed` (típicamente anonId+banditId), y
 * devuelve la propensity marginal del arm servido (con suelo `floor` para acotar la
 * varianza del estimador IPS). El arm servido sale de UN sorteo sembrado por `seed`
 * (mismo visitante → misma variante); la propensity sale de un MC independiente.
 */
export function serve(
  posteriors: ArmPosterior[],
  x: Vector,
  opts: { alpha: number; seed: string; draws?: number; floor?: number }
): ServeResult {
  const draws = opts.draws ?? 1000;
  const floor = opts.floor ?? 1e-3;
  const index = sampleArm(posteriors, x, opts.alpha, mulberry32(hashSeed(opts.seed)));
  const props = propensities(posteriors, x, opts.alpha, mulberry32(hashSeed(opts.seed + ":p")), draws);
  return { index, propensity: Math.max(floor, props[index]) };
}

/* ─────────────────────────── Helpers de política (para el gate) ─────────────────────────── */

/** Intercepto del arm: valor en la celda de referencia (diagnóstico, NO el promedio). */
export function interceptValue(post: ArmPosterior): number {
  return post.theta[0];
}

/** IPVPS medio predicho de un arm sobre una distribución de contextos. */
export function meanPrediction(post: ArmPosterior, contexts: Vector[]): number {
  if (contexts.length === 0) return 0; // sin contextos → 0 (no NaN: 0/0 envenenaría el gate)
  let sum = 0;
  for (const x of contexts) sum += predict(post, x);
  return sum / contexts.length;
}

/** Arm global: el mejor EN PROMEDIO sobre los contextos (la política sin segmentar). */
export function bestGlobalArm(posteriors: ArmPosterior[], contexts: Vector[]): number {
  let best = 0;
  let bestV = -Infinity;
  for (let k = 0; k < posteriors.length; k++) {
    const v = meanPrediction(posteriors[k], contexts);
    if (v > bestV) {
      bestV = v;
      best = k;
    }
  }
  return best;
}

/** Valor (en el MODELO) de la política CONTEXTUAL greedy: media_x max_k μ_k(x). */
export function contextualPolicyValue(posteriors: ArmPosterior[], contexts: Vector[]): number {
  if (contexts.length === 0) return 0; // sin contextos → 0 (no NaN)
  let sum = 0;
  for (const x of contexts) {
    let best = -Infinity;
    for (const post of posteriors) best = Math.max(best, predict(post, x));
    sum += best;
  }
  return sum / contexts.length;
}

/** Valor (en el MODELO) de la mejor política GLOBAL: max_k media_x μ_k(x). */
export function globalPolicyValue(posteriors: ArmPosterior[], contexts: Vector[]): number {
  return meanPrediction(posteriors[bestGlobalArm(posteriors, contexts)], contexts);
}

/**
 * "Lift contextual estimado": cuánto cree el modelo que gana personalizando vs
 * servir un único arm global. Es ≥ 0 por construcción (Jensen: el argmax por
 * contexto ≥ la mejor elección fija). Es el escalar que vigila el gate:
 *   · A/A (sin señal) + shrinkage → ≈ 0 (no inventa personalización).
 *   · señal real + datos → crece (recupera el óptimo por segmento).
 */
export function estimatedContextualLift(posteriors: ArmPosterior[], contexts: Vector[]): number {
  return contextualPolicyValue(posteriors, contexts) - globalPolicyValue(posteriors, contexts);
}

/* ─────────────────────────── Evaluación off-policy (OPE) ─────────────────────────── */

/**
 * Una muestra logueada para evaluar OFF-POLICY el valor de una política objetivo sin
 * desplegarla. `loggedProb` es la propensity con la que la política de logging eligió
 * la acción observada (DEBE ser > 0 donde la objetivo tenga probabilidad: soporte
 * común). Sin un propensity logging correcto, el estimador sale sesgado.
 */
export interface OpeSample {
  reward: number;
  targetProb: number; // π_objetivo(a | x)
  loggedProb: number; // π_logging(a | x), la propensity logueada
}

/** IPS: estimador INSESGADO del valor de la política objetivo. E[IPS] = V(π_objetivo). */
export function ipsValue(samples: OpeSample[]): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (const s of samples) sum += (s.reward * s.targetProb) / s.loggedProb;
  return sum / samples.length;
}

/** SNIPS: IPS auto-normalizado. Sesgo O(1/n) pero mucha menos varianza; consistente. */
export function snipsValue(samples: OpeSample[]): number {
  let num = 0;
  let den = 0;
  for (const s of samples) {
    const w = s.targetProb / s.loggedProb;
    num += w * s.reward;
    den += w;
  }
  return den > 0 ? num / den : 0;
}

/* ─────────────────────────── Winsorizado de la recompensa ─────────────────────────── */

/** Tope genérico de recompensa (IPVPS) para que un outlier no domine el agregado.
 *  Un valor NO finito (NaN/Inf de un dato corrupto) se neutraliza a 0: jamás debe
 *  propagarse a `b`/`θ` y envenenar el posterior del arm entero. */
export function winsorize(value: number, cap: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(Math.max(0, value), cap);
}

/* ─────────────────────────── Álgebra lineal densa (matrices pequeñas) ─────────────────────────── */

/** Producto escalar a·b. */
export function dot(a: Vector, b: Vector): number {
  let s = 0;
  for (let i = 0; i < a.length; i++) s += a[i] * b[i];
  return s;
}

/** Producto matriz-vector M·v. */
export function matVec(M: Matrix, v: Vector): Vector {
  const n = M.length;
  const out = new Array(n).fill(0);
  for (let i = 0; i < n; i++) {
    const row = M[i];
    let s = 0;
    for (let j = 0; j < row.length; j++) s += row[j] * v[j];
    out[i] = s;
  }
  return out;
}

/** Forma cuadrática vᵀ·M·v. */
export function quadForm(M: Matrix, v: Vector): number {
  let s = 0;
  for (let i = 0; i < v.length; i++) {
    const vi = v[i];
    if (vi === 0) continue;
    const row = M[i];
    for (let j = 0; j < v.length; j++) {
      const vj = v[j];
      if (vj !== 0) s += vi * row[j] * vj;
    }
  }
  return s;
}

/**
 * Inversa por Gauss-Jordan con pivote parcial. A es SPD (ridge λ>0) → nunca
 * singular; si lo fuera, lanza (preferimos romper a devolver basura silenciosa).
 */
export function invert(M: Matrix): Matrix {
  const n = M.length;
  const a: Matrix = M.map((row, i) => {
    const id = new Array(n).fill(0);
    id[i] = 1;
    return [...row, ...id];
  });
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(a[r][col]) > Math.abs(a[piv][col])) piv = r;
    }
    if (Math.abs(a[piv][col]) < 1e-12) throw new Error("bandit.invert: matriz singular");
    if (piv !== col) {
      const tmp = a[col];
      a[col] = a[piv];
      a[piv] = tmp;
    }
    const pivVal = a[col][col];
    for (let j = 0; j < 2 * n; j++) a[col][j] /= pivVal;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = a[r][col];
      if (f === 0) continue;
      for (let j = 0; j < 2 * n; j++) a[r][j] -= f * a[col][j];
    }
  }
  return a.map((row) => row.slice(n));
}

/* ─────────────────────────── PRNG determinista + gaussiana ─────────────────────────── */

/** mulberry32: PRNG rápido y determinista en [0,1). Mismo que el gate de stats. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash de cadena → semilla uint32 (cyrb53 reducido). Para sembrar el sorteo sticky. */
export function hashSeed(s: string): number {
  let h1 = 0xdeadbeef ^ s.length;
  let h2 = 0x41c6ce57 ^ s.length;
  for (let i = 0; i < s.length; i++) {
    const ch = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  return h1 >>> 0;
}

/** N(0,1) por Box-Muller a partir de un PRNG uniforme. */
export function gaussian(rng: () => number): number {
  let u1 = rng();
  if (u1 < 1e-12) u1 = 1e-12;
  const u2 = rng();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}
